import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, UserButton } from '@clerk/clerk-react';
import axios from 'axios';
import useIsMobile from '../hooks/useIsMobile';

export default function History() {
  const navigate           = useNavigate();
  const { getToken, user } = useAuth();
  const isMobile           = useIsMobile();

  const [analyses, setAnalyses] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [deleting, setDeleting] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    fetchHistory();
    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchHistory() {
    try {
      const token = await getToken();
      const res = await axios.get(
        process.env.REACT_APP_API_URL + '/api/history',
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setAnalyses(res.data);
    } catch (err) {
      setError('Could not load history. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchPlan() {
    try {
      const token = await getToken();
      const res = await axios.get(
        process.env.REACT_APP_API_URL + '/api/payments/my-plan',
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setPlanInfo(res.data);
    } catch (err) {
      console.log('Plan fetch failed:', err.message);
    }
  }

  async function openAnalysis(id) {
    try {
      const token = await getToken();
      const res = await axios.get(
        process.env.REACT_APP_API_URL + '/api/history/' + id,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      navigate('/results', { state: { results: res.data } });
    } catch (err) {
      setError('Could not load this analysis. Please try again.');
    }
  }

  async function deleteAnalysis(id) {
    setDeleting(id);
    try {
      const token = await getToken();
      await axios.delete(
        process.env.REACT_APP_API_URL + '/api/history/' + id,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setAnalyses(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError('Could not delete this analysis.');
    } finally {
      setDeleting(null);
    }
  }

  async function handleReferral() {
    try {
      const token = await getToken();
      const res = await axios.get(
        process.env.REACT_APP_API_URL + '/api/referral/my-code',
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const link = res.data.link;
      navigator.clipboard.writeText(link);
      alert(
        '🎁 Referral link copied!\n\n' +
        link + '\n\n' +
        'How it works:\n' +
        '• Share this link with a friend\n' +
        '• When they purchase any paid plan\n' +
        '• Both of you get 1 BONUS analysis\n' +
        '• Bonus type matches their purchase:\n' +
        '  ResumePlus/Pro → 1 extra Resume analysis each\n' +
        '  InterviewPlus/Pro → 1 extra Interview analysis each\n' +
        '  Elite → 1 extra Resume + 1 extra Interview each\n\n' +
        'Note: Bonus credited only on first purchase, not on signup.'
      );
    } catch (err) {
      alert('Could not fetch referral link. Please try again.');
    }
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  }

  function scoreColor(score) {
    if (score >= 70) return '#00D4AA';
    if (score >= 50) return '#f5a623';
    return '#ff6b6b';
  }

  function resumeCount(p) {
    if (!p) return null;
    if (!p.free_analysis_used) {
      return p.resume_analyses_remaining > 0
        ? p.resume_analyses_remaining + 1
        : '1 free';
    }
    return p.resume_analyses_remaining;
  }

  function interviewCount(p) {
    if (!p) return null;
    if (!p.free_analysis_used) {
      return p.interview_analyses_remaining > 0
        ? p.interview_analyses_remaining + 1
        : '1 free';
    }
    return p.interview_analyses_remaining;
  }

  function resumeColor(p) {
    if (!p) return '#00D4AA';
    if (!p.free_analysis_used) return '#00D4AA';
    return p.resume_analyses_remaining > 0 ? '#00D4AA' : '#ff6b6b';
  }

  function interviewColor(p) {
    if (!p) return '#5b8cff';
    if (!p.free_analysis_used) return '#5b8cff';
    return p.interview_analyses_remaining > 0 ? '#5b8cff' : '#ff6b6b';
  }

  const firstName = user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || 'there';

  const pillStyle = {
    background: '#141B2D',
    border: '1px solid #1a2640',
    borderRadius: '6px',
    padding: '0.3rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  };

  return (
    <div style={{ background: '#0A0F1E', minHeight: '100vh', width: '100%', color: 'white', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' }}>

      {/* ── Navbar ── */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
        borderBottom: '1px solid #1a2640',
        position: 'sticky', top: 0, background: '#0A0F1E', zIndex: 100,
        flexWrap: 'wrap', gap: '0.5rem',
      }}>

        {/* Left — Logo + Pricing */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.75rem' : '1.5rem' }}>
          <span onClick={() => navigate('/')}
            style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: isMobile ? '1.1rem' : '1.4rem', cursor: 'pointer' }}>
            CrackPrep
          </span>
          <button onClick={() => navigate('/pricing')}
            style={{ background: 'transparent', color: '#8899aa', border: 'none', cursor: 'pointer', fontSize: isMobile ? '0.8rem' : '0.9rem', fontFamily: 'Arial, sans-serif' }}>
            Pricing
          </button>
        </div>

        {/* Right — Counters + Referral + New + Profile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'nowrap' }}>

          {/* Desktop counters */}
          {planInfo && !isMobile && (
            <>
              <div style={pillStyle}>
                <span style={{ color: '#00D4AA' }}>📝</span>
                <span style={{ color: '#8899aa' }}>Resume:</span>
                <span style={{ color: resumeColor(planInfo), fontWeight: 'bold' }}>
                  {resumeCount(planInfo)}
                </span>
              </div>
              <div style={pillStyle}>
                <span style={{ color: '#5b8cff' }}>🎯</span>
                <span style={{ color: '#8899aa' }}>Interview:</span>
                <span style={{ color: interviewColor(planInfo), fontWeight: 'bold' }}>
                  {interviewCount(planInfo)}
                </span>
              </div>
            </>
          )}

          {/* Mobile counters */}
          {planInfo && isMobile && (
            <div style={{ fontSize: '0.65rem', color: '#8899aa', lineHeight: 1.4, textAlign: 'right' }}>
              <div><span style={{ color: '#00D4AA' }}>R:</span> <span style={{ color: resumeColor(planInfo) }}>{resumeCount(planInfo)}</span></div>
              <div><span style={{ color: '#5b8cff' }}>I:</span> <span style={{ color: interviewColor(planInfo) }}>{interviewCount(planInfo)}</span></div>
            </div>
          )}

          {/* Referral button */}
          <button onClick={handleReferral}
            style={{ background: 'transparent', color: '#f5a623', border: '1px solid #f5a623', padding: isMobile ? '0.35rem 0.5rem' : '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: isMobile ? '0.7rem' : '0.85rem', whiteSpace: 'nowrap' }}>
            🎁 {isMobile ? '' : 'Refer'}
          </button>

          {/* New Analysis button */}
          <button onClick={() => navigate('/analyze')}
            style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: isMobile ? '0.35rem 0.5rem' : '0.4rem 0.875rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.85rem', whiteSpace: 'nowrap' }}>
            + New
          </button>

          {/* Profile / Sign out */}
          <UserButton afterSignOutUrl="/" />
        </div>
      </nav>

      {/* ── Welcome banner ── */}
      <div style={{ padding: isMobile ? '1.25rem 1rem 0.5rem' : '2rem 2rem 0.5rem' }}>
        <h1 style={{ fontSize: isMobile ? '1.3rem' : '1.8rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
          Welcome back, <span style={{ color: '#00D4AA' }}>{firstName}</span> 👋
        </h1>
        <p style={{ color: '#8899aa', fontSize: '0.85rem', marginBottom: 0 }}>
          {analyses.length > 0
            ? 'Here are your past analyses. Click any to view the full results.'
            : 'No analyses yet. Run your first one — it\'s completely free.'}
        </p>
      </div>

      {/* ── History disclaimer ── */}
      {planInfo && (
        <div style={{
          margin: isMobile ? '0.75rem 1rem 0' : '0.75rem 2rem 0',
          background: '#141B2D',
          border: '1px solid #1a2640',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#8899aa',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          boxSizing: 'border-box',
        }}>
          <span style={{ color: '#f5a623', flexShrink: 0 }}>⚠</span>
          <span>
            You have a maximum history capacity of{' '}
            <strong style={{ color: 'white' }}>
              {Math.max(
                planInfo.resume_history_limit || 1,
                planInfo.interview_history_limit || 1
              )}{' '}
              {Math.max(planInfo.resume_history_limit || 1, planInfo.interview_history_limit || 1) === 1 ? 'analysis' : 'analyses'}
            </strong>
            . Older ones will be auto-deleted when the limit is reached — please download PDFs for future reference.
          </span>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ padding: isMobile ? '1rem' : '1.25rem 2rem' }}>

        {/* Error */}
        {error && (
          <div style={{ background: '#1a0a0a', border: '1px solid #3a1010', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', color: '#ff6b6b', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#8899aa' }}>
            Loading your analyses...
          </div>
        )}

        {/* Empty state */}
        {!loading && analyses.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <p style={{ color: '#8899aa', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Run your first analysis to see your ATS score, resume fixes, and interview prep.
            </p>
            <button onClick={() => navigate('/analyze')}
              style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.875rem 2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>
              Analyze My Resume Free →
            </button>
          </div>
        )}

        {/* Analysis list */}
        {analyses.map(a => (
          <div key={a.id} style={{
            background: '#141B2D',
            borderRadius: '12px',
            padding: isMobile ? '0.875rem' : '1.25rem',
            marginBottom: '0.75rem',
            border: '1px solid #1a2640',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>

              {/* Score */}
              <div style={{ minWidth: isMobile ? '44px' : '56px', textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 'bold', color: scoreColor(a.ats_score), lineHeight: 1 }}>
                  {a.ats_score}
                </div>
                <div style={{ fontSize: '0.6rem', color: '#8899aa' }}>ATS</div>
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.15rem', fontSize: isMobile ? '0.82rem' : '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.target_title} @ {a.target_company}
                </div>
                <div style={{ color: '#8899aa', fontSize: isMobile ? '0.72rem' : '0.75rem', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  From: {a.current_title} @ {a.current_company}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {a.has_resume    && <span style={{ background: '#0d2018', color: '#00D4AA', fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: '8px', border: '1px solid #1a4030', whiteSpace: 'nowrap' }}>📝 Resume</span>}
                  {a.has_interview && <span style={{ background: '#0d1a2e', color: '#5b8cff', fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: '8px', border: '1px solid #1a2a4a', whiteSpace: 'nowrap' }}>🎯 Interview</span>}
                  <span style={{ color: '#8899aa', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{formatDate(a.created_at)}</span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flexShrink: 0 }}>
                <button
                  onClick={() => openAnalysis(a.id)}
                  style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.8rem', whiteSpace: 'nowrap' }}>
                  Open
                </button>
                <button
                  onClick={() => deleteAnalysis(a.id)}
                  disabled={deleting === a.id}
                  style={{ background: 'transparent', color: '#ff6b6b', border: '1px solid #ff6b6b', padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontSize: isMobile ? '0.75rem' : '0.8rem', whiteSpace: 'nowrap' }}>
                  {deleting === a.id ? '...' : 'Del'}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Count */}
        {analyses.length > 0 && (
          <p style={{ color: '#8899aa', fontSize: '0.8rem', textAlign: 'center', marginTop: '0.75rem' }}>
            {analyses.length} {analyses.length === 1 ? 'analysis' : 'analyses'} saved
          </p>
        )}
      </div>
    </div>
  );
}